<template>
  <div class="max-w-5xl w-full mx-auto space-y-5">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Customer Operations</h1>
      <p class="text-sm text-gray-600 mt-1">
        Find an order, understand what happened, and share the right customer self-service link.
      </p>
    </div>

    <form class="border rounded-lg bg-white p-4" :aria-busy="loading" @submit.prevent="search()">
      <label for="customer-search" class="block text-sm font-semibold text-gray-800 mb-2">
        Order or customer
      </label>
      <div class="flex flex-col sm:flex-row gap-2">
        <input
          id="customer-search"
          v-model.trim="query"
          class="border rounded-md px-3 py-2 flex-1"
          type="search"
          autocomplete="off"
          placeholder="Order number, reference, customer email or name"
        />
        <button
          class="rounded-md bg-blue-700 text-white px-5 py-2 font-semibold disabled:opacity-50"
          type="submit"
          :disabled="loading || query.length < 3"
        >
          {{ loading ? 'Searching…' : 'Search' }}
        </button>
      </div>
      <p class="text-xs text-gray-500 mt-2">
        Results are limited to your organisation and university access.
      </p>
    </form>

    <div v-if="error" role="alert" class="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
      {{ error }}
    </div>

    <p v-if="copyMessage" class="sr-only" aria-live="polite">{{ copyMessage }}</p>

    <div v-if="searched && !loading && !results.length && !error" class="border rounded-lg bg-white p-8 text-center">
      <h2 class="font-semibold text-gray-800">No matching orders</h2>
      <p class="text-sm text-gray-600 mt-1">Check the order reference, email address, or spelling of the name.</p>
    </div>

    <div v-if="results.length" class="space-y-4" :aria-busy="loading">
      <p class="text-sm text-gray-600" aria-live="polite">Showing {{ results.length }} {{ results.length === 1 ? 'order' : 'orders' }} · Page {{ page }}</p>
      <article v-for="result in results" :key="`${result.source.type}:${result.id}`" class="border rounded-lg bg-white shadow-sm overflow-hidden">
        <header class="p-4 border-b bg-gray-50 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-semibold text-gray-900">{{ result.customer.name || 'Customer name unavailable' }}</h2>
              <span class="text-xs font-semibold rounded-full bg-white border px-2 py-1">{{ result.source.label }}</span>
              <span :class="paymentClass(result.payment_state)" class="text-xs font-semibold rounded-full px-2 py-1">
                {{ paymentLabel(result.payment_state) }}
              </span>
            </div>
            <a v-if="result.customer.email" class="text-sm text-blue-700 hover:underline" :href="`mailto:${result.customer.email}`">
              {{ result.customer.email }}
            </a>
          </div>
          <div class="text-sm md:text-right">
            <div class="font-medium text-gray-800">{{ result.university.name || result.university.slug }}</div>
            <time class="text-gray-600" :datetime="result.ceremony.datetime || ''">{{ formatDate(result.ceremony.datetime) }}</time>
          </div>
        </header>

        <div class="p-4 space-y-4">
          <div :class="summaryClass(result)" class="rounded-md border px-3 py-2 text-sm font-medium">
            {{ resultSummary(result) }}
          </div>
          <dl class="grid sm:grid-cols-3 gap-3 text-sm">
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500">Order</dt>
              <dd class="font-mono break-all">{{ result.source.external_order_id || result.id }}</dd>
            </div>
            <div v-if="result.source.external_reference">
              <dt class="text-xs uppercase tracking-wide text-gray-500">Partner reference</dt>
              <dd class="font-mono break-all">{{ result.source.external_reference }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500">Payment</dt>
              <dd>{{ humanStatus(result.payment_status) }}</dd>
            </div>
            <div v-if="result.created_at">
              <dt class="text-xs uppercase tracking-wide text-gray-500">Ordered</dt>
              <dd><time :datetime="result.created_at">{{ formatOrderDate(result.created_at) }}</time></dd>
            </div>
          </dl>

          <div class="space-y-2">
            <div v-for="(product, index) in result.products" :key="`${product.type}:${product.reference_id || index}`" class="border rounded-md p-4 space-y-3">
              <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                <div class="flex items-center gap-2">
                  <h3 class="font-semibold text-gray-900">{{ productLabel(product.type) }}</h3>
                  <span :class="statusClass(product.status)" class="text-xs font-semibold rounded-full px-2 py-1">
                    {{ humanStatus(product.status) }}
                  </span>
                  <span v-if="product.type === 'clip' && product.footage_state" :class="footageClass(product.footage_state)" class="text-xs font-semibold rounded-full px-2 py-1">
                    {{ footageLabel(product.footage_state) }}
                  </span>
                </div>
                <p v-if="formatMoney(product)" class="text-sm text-gray-600 mt-1">{{ formatMoney(product) }}</p>
                </div>
                <div v-if="primaryAction(product)" class="flex flex-wrap gap-2">
                  <button type="button" class="border rounded-md px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-600" :aria-label="`${primaryAction(product).label} for ${result.customer.name || 'customer'}`" @click="openCustomerAction(result, product, primaryAction(product))">
                    {{ primaryAction(product).label }}
                  </button>
                  <button type="button" class="border rounded-md px-3 py-2 text-sm font-semibold hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-600" :aria-label="`Copy ${productLabel(product.type).toLowerCase()} link for ${result.customer.name || 'customer'}`" @click="copyLink(result, product, primaryAction(product), productLabel(product.type))">
                    {{ copiedPath === primaryAction(product).handle ? 'Copied' : 'Copy customer link' }}
                  </button>
                </div>
              </div>

              <div v-if="product.access_revoked" role="status" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                Access has been revoked. Do not send an earlier customer link.
              </div>
              <div v-else-if="!product.customer_record_exists" role="status" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                The customer fulfilment record is missing. Escalate this order; do not construct or send a link manually.
              </div>
              <div v-else-if="product.delivery_blocked_by_payment" role="status" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {{ paymentBlockMessage(result.payment_state) }}
              </div>
              <p v-else-if="product.delivery_link_restricted" class="text-sm text-gray-600">Customer-link access is restricted for your role.</p>
              <div v-else-if="product.type === 'clip' && product.footage_state !== 'ready'" :class="footageMessageClass(product.footage_state)" class="rounded-md border px-3 py-2 text-sm">
                {{ footageMessage(product.footage_state) }}
              </div>
              <p v-else-if="!primaryAction(product) && !secondaryActions(product).length" class="text-sm text-gray-600">Customer links will appear when this product is ready.</p>

              <details v-if="secondaryActions(product).length" class="rounded-md border bg-gray-50 p-3">
                <summary class="cursor-pointer font-semibold text-sm text-gray-800 focus-visible:ring-2 focus-visible:ring-blue-600">Customer self-service</summary>
                <p class="text-xs text-gray-600 mt-2">These verified links are for this exact customer record. Opening them does not make a change, but the customer can submit changes from the page.</p>
                <ul class="mt-3 space-y-3">
                  <li v-for="action in secondaryActions(product)" :key="action.kind" class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <div class="text-sm font-semibold text-gray-900">{{ action.label }}</div>
                      <div class="text-xs text-gray-600">{{ action.consequence }}</div>
                    </div>
                    <div class="flex gap-2 shrink-0">
                      <button type="button" class="border rounded-md bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-600" :aria-label="`${action.label} for ${result.customer.name || 'customer'}`" @click="openCustomerAction(result, product, action)">Open</button>
                      <button type="button" class="border rounded-md bg-white px-3 py-2 text-sm font-semibold hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-600" :aria-label="`Copy ${action.label.toLowerCase()} link for ${result.customer.name || 'customer'}`" @click="copyLink(result, product, action, action.label)">{{ copiedPath === action.handle ? 'Copied' : 'Copy link' }}</button>
                    </div>
                  </li>
                </ul>
              </details>
            </div>
            <p v-if="!result.products.length" class="text-sm text-gray-500">No MGC fulfilment items are attached to this order.</p>
          </div>
        </div>
      </article>
      <nav v-if="page > 1 || hasMore" class="flex justify-between items-center" aria-label="Search results pages">
        <button type="button" class="border rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50" :disabled="loading || page === 1" @click="search(page - 1)">
          Previous
        </button>
        <button type="button" class="border rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50" :disabled="loading || !hasMore" @click="search(page + 1)">
          Next
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CustomerOperations',
  data() {
    return {
      query: '',
      results: [],
      loading: false,
      searched: false,
      error: '',
      copiedPath: '',
      copyMessage: '',
      page: 1,
      hasMore: false,
    }
  },
  methods: {
    async search(page = 1) {
      if (this.query.length < 3 || this.loading) return
      this.loading = true
      this.error = ''
      this.searched = true
      try {
        const response = await axios.post('/admin/search', {
          mode: 'customer_operations',
          query: this.query,
          page,
          page_size: 10,
        })
        this.results = Array.isArray(response.data?.results) ? response.data.results : []
        this.page = Number(response.data?.page) || page
        this.hasMore = Boolean(response.data?.has_more)
      } catch (error) {
        this.results = []
        this.hasMore = false
        this.error = error.response?.data?.error || 'Unable to search Customer Operations.'
      } finally {
        this.loading = false
      }
    },
    productLabel(type) {
      return type === 'digital_download' ? 'Full ceremony video' : 'Personalised clip'
    },
    humanStatus(value) {
      const text = String(value || 'unknown').replace(/_/g, ' ')
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    statusClass(status) {
      if (status === 'ready') return 'bg-green-100 text-green-800'
      if (status === 'failed') return 'bg-red-100 text-red-800'
      if (status === 'processing') return 'bg-blue-100 text-blue-800'
      return 'bg-yellow-100 text-yellow-800'
    },
    footageLabel(state) {
      return {
        ready: 'Footage ready',
        processing: 'Footage processing',
        failed: 'Footage failed',
        missing: 'Footage missing',
      }[state] || 'Footage unknown'
    },
    footageClass(state) {
      if (state === 'ready') return 'bg-green-100 text-green-800'
      if (state === 'processing') return 'bg-blue-100 text-blue-800'
      return 'bg-red-100 text-red-800'
    },
    footageMessage(state) {
      if (state === 'missing') return 'No active clip footage is available. Do not send a moment-selection link.'
      if (state === 'failed') return 'Clip footage processing failed or was blocked. Escalate before sending a moment-selection link.'
      return 'Clip footage is still processing. Moment selection will become available when the footage is live.'
    },
    footageMessageClass(state) {
      return state === 'processing'
        ? 'border-blue-200 bg-blue-50 text-blue-900'
        : 'border-red-200 bg-red-50 text-red-900'
    },
    paymentLabel(state) {
      return {
        paid: 'Paid',
        refunded: 'Refunded',
        partially_refunded: 'Partially refunded',
        failed: 'Payment failed',
        pending: 'Payment pending',
      }[state] || 'Payment unknown'
    },
    paymentClass(state) {
      if (state === 'paid') return 'bg-green-100 text-green-800'
      if (state === 'refunded' || state === 'failed') return 'bg-red-100 text-red-800'
      return 'bg-yellow-100 text-yellow-900'
    },
    primaryAction(product) {
      return (product.customer_actions || []).find(action => ['view_clip', 'view_download'].includes(action.kind)) || null
    },
    secondaryActions(product) {
      return (product.customer_actions || []).filter(action => !['view_clip', 'view_download'].includes(action.kind))
    },
    paymentBlockMessage(state) {
      if (state === 'refunded') return 'Refunded — do not send customer access links.'
      if (state === 'partially_refunded') return 'Partially refunded — confirm the remaining entitlement before sending a link.'
      return 'Payment needs attention before a customer link can be sent.'
    },
    resultSummary(result) {
      const products = result.products || []
      if (products.some(product => !product.customer_record_exists)) return 'A customer fulfilment record is missing. Escalate; do not send a link.'
      if (products.some(product => product.access_revoked)) return 'Customer access has been revoked. Do not send a link.'
      if (result.payment_state === 'refunded') return 'Refunded — customer access must remain blocked.'
      if (result.payment_state === 'partially_refunded') return 'Partially refunded — review the remaining entitlement before responding.'
      if (result.payment_state !== 'paid') return 'Payment needs attention before a product can be sent.'
      if (products.some(product => product.delivery_link_restricted)) return 'Customer-link access is restricted for your role.'
      const incompleteClip = products.find(product => product.type === 'clip' && product.status !== 'ready')
      if (incompleteClip?.footage_state === 'failed') return 'Clip footage processing failed or was blocked and needs MGC review.'
      if (incompleteClip?.footage_state === 'missing') return 'Payment received, but no active clip footage is available.'
      if (incompleteClip?.footage_state === 'processing') return 'Payment received. Clip footage is still processing.'
      const statuses = products.map(product => product.status)
      if (!statuses.length) return 'No fulfilled products are attached to this order yet.'
      if (statuses.includes('failed')) return 'A product failed and needs MGC review.'
      if (statuses.every(status => status === 'ready')) return 'Everything purchased is ready to send to the customer.'
      if (statuses.includes('processing')) return 'Payment received. One or more products are still processing.'
      return 'Payment received. The purchased product is waiting to be prepared.'
    },
    summaryClass(result) {
      const summary = this.resultSummary(result)
      if (summary.includes('ready to send')) return 'border-green-200 bg-green-50 text-green-900'
      if (summary.includes('failed') || summary.includes('missing') || summary.includes('revoked') || summary.includes('Payment needs') || summary.includes('Refunded')) return 'border-red-200 bg-red-50 text-red-900'
      if (summary.includes('Partially refunded') || summary.includes('restricted')) return 'border-yellow-300 bg-yellow-50 text-yellow-900'
      return 'border-blue-200 bg-blue-50 text-blue-900'
    },
    formatDate(value) {
      if (!value) return 'Ceremony date unavailable'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
    },
    formatOrderDate(value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()
    },
    formatMoney(product) {
      const amount = product.total_amount ?? product.unit_amount
      if (amount === null || amount === undefined || !product.currency) return ''
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: product.currency.toUpperCase(),
        }).format(Number(amount) / 100)
      } catch (_) {
        return `${product.currency.toUpperCase()} ${(Number(amount) / 100).toFixed(2)}`
      }
    },
    async resolveCustomerAction(action, interaction) {
      const response = await axios.post('/admin/search', {
        mode: 'customer_operations_action',
        handle: action.handle,
        interaction,
      })
      return response.data?.path || ''
    },
    async openCustomerAction(result, product, action) {
      const pendingWindow = window.open('about:blank', '_blank')
      if (pendingWindow) pendingWindow.opener = null
      let path = ''
      try {
        path = await this.resolveCustomerAction(action, 'opened')
      } catch (_) {
        if (pendingWindow) pendingWindow.close()
        this.error = 'Unable to record this customer-link action. The link was not opened.'
        return
      }
      const url = new URL(path, window.location.origin).toString()
      if (pendingWindow) {
        pendingWindow.location.replace(url)
      } else {
        this.error = 'The link was verified, but your browser blocked the new window.'
      }
    },
    async copyLink(result, product, action, label = 'Customer') {
      let path = ''
      try {
        path = await this.resolveCustomerAction(action, 'copied')
      } catch (_) {
        this.copyMessage = 'Unable to record this customer-link action.'
        this.error = 'Unable to record this customer-link action. The link was not copied.'
        return
      }
      const url = new URL(path, window.location.origin).toString()
      this.copyMessage = ''
      try {
        await navigator.clipboard.writeText(url)
        this.copiedPath = action.handle
        this.copyMessage = `${label} link copied.`
      } catch (_) {
        this.copyMessage = 'Unable to copy the customer link.'
        this.error = 'Unable to copy the customer link. Open the product and copy the address instead.'
      }
    },
  },
}
</script>
